import React, { useState, useRef, useEffect } from 'react';
import './MartAuthModal.css';

const BASE = 'https://api.atrmart.in/martapi';

// Optimized, lightweight fetch wrapper matching your backend error payload structure
const api = async (endpoint, body) => {
  try {
    const res = await fetch(`${BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    
    let json;
    try { 
      json = await res.json(); 
    } catch { 
      json = { status: false, message: `Server returned status code: ${res.status}` }; 
    }
    
    if (!res.ok) {
      const msg = json.message
        || (json.errors ? Object.values(json.errors).flat()[0] : null)
        || `Error ${res.status}`;
      throw new Error(msg);
    }
    return json;
  } catch (err) {
    if (err instanceof TypeError) {
      throw new Error('Cannot reach API server. Please check your network or Laravel status.');
    }
    throw err;
  }
};

const MartAuthModal = ({ isOpen, onClose, onAuthSuccess, hint }) => {
  const [screen, setScreen] = useState('mobile'); 
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [timer, setTimer] = useState(0);

  const refs = useRef([]);
  const timerRef = useRef(null);

  // Countdown handler for OTP resend feature
  useEffect(() => {
    if (timer > 0) { 
      timerRef.current = setTimeout(() => setTimer(t => t - 1), 1000); 
    }
    return () => clearTimeout(timerRef.current);
  }, [timer]);

  // Wipes state clean whenever modal state shifts open/closed
  useEffect(() => {
    if (!isOpen) return;
    setScreen('mobile'); 
    setMobile(''); 
    setOtp(['', '', '', '', '', '']);
    setName(''); 
    setEmail(''); 
    setError(''); 
    setDevOtp(''); 
    setTimer(0);
  }, [isOpen]);

  // Accessibility: Closes application modal on Escape key tap
  useEffect(() => {
    if (!isOpen) return;
    const esc = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Handles text box value changes and moves focus right
  const oChange = (i, v) => {
    if (!/^\d?$/.test(v)) return;
    const n = [...otp]; 
    n[i] = v; 
    setOtp(n);
    if (v && i < 5) refs.current[i + 1]?.focus();
  };
  
  // Custom Keyboard Backspace event layout with index focus tracking
  const oKey = (i, e) => {
    if (e.key === 'Backspace') {
      if (!otp[i] && i > 0) {
        const n = [...otp];
        n[i - 1] = ''; // Clears preceding box index
        setOtp(n);
        refs.current[i - 1]?.focus();
      }
    }
  };

  const doSendOtp = async (mob) => {
    const res = await api('/user/send-otp', { mobile: mob });
    if (res.dev_otp) setDevOtp(res.dev_otp);
    setScreen('otp');
    setTimer(30);
    setTimeout(() => refs.current[0]?.focus(), 100);
  };

  const onCheckMobile = async e => {
    e.preventDefault();
    if (!/^\d{10}$/.test(mobile)) { 
      setError('Enter a valid 10-digit mobile number.'); 
      return; 
    }
    setError(''); 
    setLoading(true);
    try {
      const res = await api('/user/check-mobile', { mobile });
      if (res.exists) { 
        await doSendOtp(mobile); 
      } else { 
        setScreen('register'); 
      }
    } catch (err) { 
      setError(err.message); 
    } finally { 
      setLoading(false); 
    }
  };

  const onVerifyOtp = async e => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 6) { 
      setError('Enter all 6 OTP digits.'); 
      return; 
    }
    setError(''); 
    setLoading(true);
    try {
      const res = await api('/user/verify-otp', { mobile, otp: code });
      
      // Sync login profiles variables cleanly across matching local memory keys
      localStorage.setItem('user_token', res.token); 
      localStorage.setItem('user', JSON.stringify(res.user));
      
      if (onAuthSuccess) {
        onAuthSuccess(res.user);
      }
      onClose();
    } catch (err) { 
      setError(err.message); 
    } finally { 
      setLoading(false); 
    }
  };

  const onResend = async () => {
    if (timer > 0 || loading) return;
    setError(''); 
    setLoading(true);
    try {
      setOtp(['', '', '', '', '', '']);
      await doSendOtp(mobile);
    } catch (err) { 
      setError(err.message); 
    } finally { 
      setLoading(false); 
    }
  };

  const onRegister = async e => {
    e.preventDefault();
    if (!name.trim()) { 
      setError('Enter your full name.'); 
      return; 
    }
    setError(''); 
    setLoading(true);
    try {
      await api('/user/register', { name: name.trim(), mobile, email: email || undefined });
      await doSendOtp(mobile);
    } catch (err) { 
      setError(err.message); 
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div className="mart-auth-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mart-auth-modal">
        <div className="mart-auth-modal-header">
          <div className="mart-auth-header-brand">
            <div className="mart-auth-header-hex">M</div>
            <span className="mart-auth-header-name">ATR MART</span>
          </div>
          {hint && <div className="mart-auth-hint-text">{hint}</div>}
          <h2 className="mart-auth-modal-title">
            {screen === 'mobile' ? 'Sign in to continue' : screen === 'otp' ? 'Verify your number' : 'Create account'}
          </h2>
          <button className="mart-auth-close-btn" onClick={onClose} type="button">&times;</button>
        </div>

        <div className="mart-auth-modal-body">
          {error && <div className="mart-auth-msg-error">&#9888; {error}</div>}
          {devOtp && <div className="mart-auth-msg-dev">Dev Test Mode OTP: {devOtp}</div>}

          {screen === 'mobile' && (
            <form onSubmit={onCheckMobile}>
              <div className="mart-auth-input-group">
                <label className="mart-auth-label">MOBILE NUMBER</label>
                <div className="mart-auth-mobile-wrap">
                  <span className="mart-auth-prefix">+91</span>
                  <input 
                    type="tel" 
                    inputMode="numeric" 
                    maxLength={10} 
                    placeholder="10-digit number"
                    value={mobile} 
                    onChange={e => { setError(''); setMobile(e.target.value.replace(/\D/g, '')); }} 
                    autoFocus
                  />
                </div>
              </div>
              <button className="mart-auth-submit" type="submit" disabled={loading || mobile.length !== 10}>
                {loading ? 'Checking…' : 'Continue →'}
              </button>
            </form>
          )}

          {screen === 'otp' && (
            <form onSubmit={onVerifyOtp}>
              <div className="mart-auth-input-group">
                <label className="mart-auth-label">ENTER 6-DIGIT OTP</label>
                <div className="mart-auth-otp-row">
                  {otp.map((d, i) => (
                    <input 
                      key={i} 
                      ref={el => refs.current[i] = el}
                      className="mart-auth-otp-box" 
                      type="tel" 
                      inputMode="numeric" 
                      maxLength={1} 
                      value={d}
                      onChange={e => oChange(i, e.target.value)} 
                      onKeyDown={e => oKey(i, e)}
                    />
                  ))}
                </div>
                <div className="mart-auth-resend-row">
                  {timer > 0 ? (
                    `Resend in ${timer}s`
                  ) : (
                    <span className="mart-auth-resend-link" onClick={onResend}>
                      {loading ? 'Sending…' : 'Resend OTP'}
                    </span>
                  )}
                </div>
              </div>
              <button className="mart-auth-submit" type="submit" disabled={loading || otp.join('').length < 6}>
                {loading ? 'Verifying…' : '✓ Verify & Login'}
              </button>
            </form>
          )}

          {screen === 'register' && (
            <form onSubmit={onRegister}>
              <div className="mart-auth-input-group">
                <label className="mart-auth-label">FULL NAME *</label>
                <input 
                  className="mart-auth-input" 
                  type="text" 
                  placeholder="Your full name"
                  value={name} 
                  onChange={e => { setError(''); setName(e.target.value); }} 
                  autoFocus 
                  required
                />
              </div>
              <div className="mart-auth-input-group">
                <label className="mart-auth-label">EMAIL (optional)</label>
                <input 
                  className="mart-auth-input" 
                  type="email" 
                  placeholder="you@example.com"
                  value={email} 
                  onChange={e => { setError(''); setEmail(e.target.value); }}
                />
              </div>
              <button className="mart-auth-submit" type="submit" disabled={loading || !name.trim()}>
                🛒 Create Account & Continue
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default MartAuthModal;